import { useCallback, useEffect, useState } from "react";
import { getPushPublicKey, subscribePush, unsubscribePush } from "../api/push";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export type PushState = {
  supported: boolean;
  configured: boolean;
  permission: NotificationPermission;
  subscribed: boolean;
  busy: boolean;
};

export function useWebPush(): {
  state: PushState;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
} {
  const [state, setState] = useState<PushState>({
    supported:
      typeof window !== "undefined" &&
      "Notification" in window &&
      "serviceWorker" in navigator &&
      "PushManager" in window,
    configured: false,
    permission: typeof Notification !== "undefined" ? Notification.permission : "default",
    subscribed: false,
    busy: false,
  });

  const refresh = useCallback(async () => {
    if (!state.supported) return;
    const { configured } = await getPushPublicKey();
    const reg = await navigator.serviceWorker.getRegistration("/priomail-sw.js");
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    setState((p) => ({
      ...p,
      configured,
      permission: Notification.permission,
      subscribed: Boolean(sub),
    }));
  }, [state.supported]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    if (!state.supported) return;
    setState((p) => ({ ...p, busy: true }));
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setState((p) => ({ ...p, permission: perm, busy: false }));
        return;
      }
      const { publicKey, configured } = await getPushPublicKey();
      if (!configured || !publicKey) {
        setState((p) => ({ ...p, configured: false, busy: false }));
        return;
      }
      const reg =
        (await navigator.serviceWorker.getRegistration("/priomail-sw.js")) ??
        (await navigator.serviceWorker.register("/priomail-sw.js"));
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
        });
      }
      const json = sub.toJSON() as { keys?: { p256dh?: string; auth?: string } };
      await subscribePush({
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh ?? arrayBufferToBase64(sub.getKey("p256dh")),
        auth: json.keys?.auth ?? arrayBufferToBase64(sub.getKey("auth")),
        ua: navigator.userAgent,
      });
      setState((p) => ({ ...p, configured: true, permission: "granted", subscribed: true, busy: false }));
    } catch {
      setState((p) => ({ ...p, busy: false }));
    }
  }, [state.supported]);

  const disable = useCallback(async () => {
    if (!state.supported) return;
    setState((p) => ({ ...p, busy: true }));
    try {
      const reg = await navigator.serviceWorker.getRegistration("/priomail-sw.js");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await unsubscribePush(sub.endpoint).catch(() => null);
        await sub.unsubscribe();
      }
      setState((p) => ({ ...p, subscribed: false, busy: false }));
    } catch {
      setState((p) => ({ ...p, busy: false }));
    }
  }, [state.supported]);

  return { state, enable, disable };
}
